import React, { useState, useEffect } from 'react';
import { X, ShoppingBag, Plus, Minus, Trash2, ArrowRight } from 'lucide-react';
import { cartService } from '../../services/api';
import { Link, useNavigate } from 'react-router-dom';

const CartDrawer = ({ isOpen, onClose }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      loadCart();
    }
  }, [isOpen]);

  const loadCart = async () => {
    try {
      setLoading(true);
      const data = await cartService.index();
      setCart(data);
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, newQty) => {
    if (newQty < 1) return;
    try {
      const data = await cartService.updateItem(itemId, newQty);
      setCart(data);
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  const removeItem = async (itemId) => {
    try {
      const data = await cartService.removeItem(itemId);
      setCart(data);
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  if (!isOpen) return null;

  const items = cart?.items || [];

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      
      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
          {/* Header */}
          <div className="px-6 py-6 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 text-red-600 rounded-xl flex items-center justify-center">
                <ShoppingBag size={20} />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 leading-tight">Your Cart</h2>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">{items.length} Items</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-900">
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {loading && !cart ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              </div>
            ) : items.length > 0 ? (
              items.map((item) => (
                <div key={item.id} className="flex gap-4 group">
                  <div className="w-20 h-20 bg-slate-100 rounded-2xl overflow-hidden flex-shrink-0 border border-slate-100">
                    <img 
                      src={item.product?.featured_image || '/placeholder-product.png'} 
                      alt={item.product?.name} 
                      className="w-full h-full object-cover transition-transform group-hover:scale-110"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-bold text-slate-900 truncate pr-4">{item.product?.name}</h3>
                      <span className="font-black text-slate-900">₹{item.price * item.quantity}</span>
                    </div>
                    {item.variant && (
                      <p className="text-xs text-red-600 font-bold uppercase tracking-wider mb-2">{item.variant.name}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center border border-slate-100 rounded-lg overflow-hidden h-8">
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-50"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-8 text-center text-sm font-bold text-slate-900">{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-50"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <button 
                        onClick={() => removeItem(item.id)}
                        className="text-slate-300 hover:text-rose-500 transition-colors p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-slate-50 text-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag size={32} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Cart is empty</h3>
                <p className="text-slate-400 text-sm mb-6">Looks like you haven't added anything yet.</p>
                <button 
                  onClick={onClose}
                  className="bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-red-600 transition-colors"
                >
                  Start Shopping
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="px-6 py-8 border-t bg-slate-50/50">
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-slate-500 text-sm">
                  <span>Subtotal</span>
                  <span className="font-bold text-slate-900">₹{cart?.subtotal || 0}</span>
                </div>
                <div className="flex justify-between text-slate-500 text-sm">
                  <span>GST (13%)</span>
                  <span className="font-bold text-slate-900">₹{cart?.tax || 0}</span>
                </div>
                {cart?.discount > 0 && (
                  <div className="flex justify-between text-red-600 text-sm">
                    <span>Discount</span>
                    <span className="font-bold">-₹{cart.discount}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg pt-3 border-t">
                  <span className="font-black text-slate-900">Total</span>
                  <span className="font-black text-red-600">₹{cart?.total || 0}</span>
                </div>
              </div>
              <button 
                onClick={() => {
                  onClose();
                  navigate('/checkout');
                }}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-2xl font-black text-lg flex items-center justify-center gap-3 transition-all shadow-xl shadow-red-200 active:scale-[0.98]"
              >
                Checkout Now
                <ArrowRight size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
